import { useCallback } from 'react'
import { useLocation } from 'wouter'
import { HugeiconsIcon } from '@hugeicons/react'
import { Trans, useLingui } from '@lingui/react/macro'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { skipToken, useSuspenseQuery } from '@apollo/client/react'

import {
  Cancel01Icon,
  Tick01Icon,
  PlusSignIcon,
  Delete01Icon
} from '@hugeicons/core-solid-rounded'

import { IconButton } from '@/components/ui/IconButton'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { useDeletePromotion } from '../hooks/useDeletePromotion'

function PromotionRowInfos({
  promotion
}: {
  promotion: GraphQL.PromotionFragmentFragment
}) {
  return (
    <>
      <div className="flex-1 px-4 py-3 text-regular text-text text-left cursor-pointer">
        {promotion.name}
      </div>
      <div className="flex-1 px-4 py-3 text-regular text-text text-left cursor-pointer">
        {promotion.priority}
      </div>
      <div className="flex-1 px-4 py-3 text-regular text-text text-left cursor-pointer">
        {
          <HugeiconsIcon
            icon={promotion.isActive ? Tick01Icon : Cancel01Icon}
            className="text-text size-5"
          />
        }
      </div>
    </>
  )
}

function PromotionRow({
  promotion
}: {
  promotion: GraphQL.PromotionFragmentFragment
}) {
  const [, navigate] = useLocation()
  const { t } = useLingui()
  const remove = useDeletePromotion(promotion.id)

  const promotionName = promotion.name

  return (
    <div
      className="flex flex-row items-center [&:nth-of-type(even)]:bg-background hover:bg-secondary-bright! rounded-small cursor-pointer"
      onClick={() => navigate(`~/app/promotion/${promotion.token}`)}
      tabIndex={0}
      aria-label={t`Open promotion`}
    >
      <PromotionRowInfos promotion={promotion} />
      <div className="w-10 flex flex-row justify-center items-center aspect-square rounded-full text-text text-regular cursor-pointer select-none transition-colors">
        <ConfirmDialog
          title={t`Delete Promotion`}
          message={t`Are you sure you want to delete the promotion with the name "${promotionName}", this cannot be reversed?`}
          onConfirm={remove}
          asChild
        >
          <IconButton
            icon={Delete01Icon}
            type="button"
            className="text-red-500"
            aria-label={t`Delete Promotion`}
          />
        </ConfirmDialog>
      </div>
    </div>
  )
}

export function PromotionsByGoodie({
  id,
  token
}: {
  id: number
  token: string
}) {
  const [, navigate] = useLocation()

  const { data } = useSuspenseQuery(
    AdminAPI.promotion.GET_FOR_GOODIE,
    id
      ? {
          variables: { goodieId: id, withTranslations: true },
          fetchPolicy: 'cache-and-network'
        }
      : skipToken
  )

  const promotions = getFragment(
    SharedAPI.promotion.PROMOTION_FRAGMENT,
    data?.getAllPromotionsForGoodie
  )

  const onAdd = useCallback(() => {
    navigate(`~/app/create-promotion/${token}`)
  }, [token, navigate])

  return (
    <div className="flex flex-col w-full p-1 gap-2">
      <div className="flex flex-row items-stretch">
        {!promotions?.length && (
          <span className="text-text text-regular text-center w-full">
            <Trans>
              To show this offer on the Feed, create an upselling option below
            </Trans>
          </span>
        )}
        {!!promotions?.length && (
          <>
            <div className="w-6 rounded-l-small bg-secondary-dark" />
            <div className="flex-1 px-4 py-2.5 text-heading3 font-medium text-white bg-secondary-dark">
              <Trans>Title</Trans>
            </div>
            <div className="flex-1 px-4 py-2.5 text-heading3 font-medium text-white bg-secondary-dark">
              <Trans>Priority</Trans>
            </div>
            <div className="flex-1 px-4 py-2.5 text-heading3 font-medium text-white bg-secondary-dark">
              <Trans>Active</Trans>
            </div>
            <div className="w-10 px-4 py-2.5 text-heading3 rounded-r-small font-medium text-white bg-secondary-dark" />
          </>
        )}
      </div>

      <div className="flex flex-col gap-0.5">
        {promotions?.map(promotion => {
          return <PromotionRow key={promotion.id} promotion={promotion} />
        })}
      </div>

      <button
        type="button"
        onClick={() => onAdd()}
        className="flex flex-row justify-center items-center p-2 border border-dashed border-text-light rounded-small gap-1"
      >
        <HugeiconsIcon icon={PlusSignIcon} className="size-5 text-text-light" />
        <span className="text-text-light text-regular">
          <Trans>Add Upselling Option</Trans>
        </span>
      </button>
    </div>
  )
}
